import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
} from "react";
import type { FlatList as RNFlatList, LayoutChangeEvent } from "react-native";
import { View } from "react-native";
import { UnistylesRuntime } from "react-native-unistyles";

import FlatList from "$src/core/components/atoms/FlatList/FlatList";
import $ from "$src/core/components/organisms/EPGChannelGrid/EPGChannelColumn/EPGChannelColumn.styles";
import EPGChannelHeader from "$src/core/components/organisms/EPGChannelGrid/EPGChannelHeader/EPGChannelHeader";
import EPGProgramBlock from "$src/core/components/organisms/EPGChannelGrid/EPGProgramBlock/EPGProgramBlock";
import type { EPGChannelColumnProps } from "$src/core/components/organisms/EPGChannelGrid/types";
import EPGSelectedChannelIndexContext from "$src/core/contexts/epg-selected-channel";
import useTabBarHeight from "$src/core/hooks/use-tab-bar-height";
import type { FixedRowProgramLayout } from "$src/core/models/epg";
import findClosestProgramLayoutIndexToInstant from "$src/core/utils/epg/find-closest-program-layout-index-to-instant";
import getEpgChannelProgramListKey from "$src/core/utils/epg/get-epg-channel-program-list-key";
import getProgramRowLayout from "$src/core/utils/epg/get-program-row-layout";
import getTimelineAnchorInstantMs from "$src/core/utils/epg/get-timeline-anchor-instant-ms";
import isEpgEntryLiveAt from "$src/core/utils/epg/is-epg-entry-live-at";
import CustomPlatform from "$src/core/utils/platform/platform";

/* Only this leaf re-renders when the selection moves, not the column or the grid. */
const ContextSelectedHeader = ({
  channelIndex,
  title,
  logo,
  logoDark,
}: {
  channelIndex: number;
  title: EPGChannelColumnProps["channel"]["title"];
  logo: EPGChannelColumnProps["channel"]["logo"];
  logoDark: EPGChannelColumnProps["channel"]["logoDark"];
}) => {
  const selectedChannelIndex = useContext(EPGSelectedChannelIndexContext);

  return (
    <EPGChannelHeader
      title={title}
      logo={logo}
      logoDark={logoDark}
      isSelected={selectedChannelIndex === channelIndex}
    />
  );
};

/** The fading-edge length is the one margin Android's focus-scroll honours, so a focused row is never left in the last few pixels. */
const FOCUS_SCROLL_INSET = CustomPlatform.select({
  androidTV: {
    start: UnistylesRuntime.getTheme().spacing(16),
    end: UnistylesRuntime.getTheme().spacing(16),
  },
  default: undefined,
});

/** Headroom only, since `initialScrollIndex` already opens the list at the anchor row; TV needs less because its paged list caps mounted columns. */
const PROGRAM_WINDOW_SIZE = CustomPlatform.select({
  tvOS: 5,
  androidTV: 4,
  default: 12,
});

const EPGChannelColumn = React.memo(
  ({
    channel,
    channelIndex,
    columnWidth,
    preferredFocusChannelId,
    preferredFocusProgramIndex,
    compactRowHeight,
    liveRowHeight,
    timelineScrollAnchor,
    programListRemountKey,
    highlightLivePrograms,
    nowMs,
    onTVProgramsFocus,
    onTVProgramNavigateToDetail,
    onTVEpgProgramFocus,
    registerProgramRef,
    onProgramFocus,
  }: EPGChannelColumnProps) => {
    const tabBarHeight = useTabBarHeight();
    const programListRef = useRef<RNFlatList<FixedRowProgramLayout> | null>(
      null,
    );
    const { programLayouts, id, title, logo, logoDark } = channel;

    const isTimelineLayoutEnabled =
      timelineScrollAnchor != null && compactRowHeight > 0;

    const liveIndex = useMemo(() => {
      if (!highlightLivePrograms || nowMs == null) {
        return null;
      }
      const index = programLayouts.findIndex(({ entry }) =>
        isEpgEntryLiveAt(entry, nowMs),
      );
      return index >= 0 ? index : null;
    }, [highlightLivePrograms, nowMs, programLayouts]);

    const getItemLayout = useMemo(
      () =>
        isTimelineLayoutEnabled
          ? getProgramRowLayout({ compactRowHeight, liveRowHeight, liveIndex })
          : undefined,
      [isTimelineLayoutEnabled, compactRowHeight, liveRowHeight, liveIndex],
    );

    const anchorIndex = useMemo(() => {
      if (!isTimelineLayoutEnabled || programLayouts.length === 0) {
        return undefined;
      }

      // The live card is the row the user looks for, and a schedule gap can put
      // the closest row next to it instead of on it, so prefer the live row.
      if (liveIndex != null) {
        return liveIndex;
      }

      return findClosestProgramLayoutIndexToInstant(
        programLayouts,
        getTimelineAnchorInstantMs(timelineScrollAnchor),
      );
    }, [
      isTimelineLayoutEnabled,
      liveIndex,
      programLayouts,
      timelineScrollAnchor,
    ]);

    const renderProgramItem = useCallback(
      ({ item, index }: { item: FixedRowProgramLayout; index: number }) => (
        <EPGProgramBlock
          ref={
            registerProgramRef
              ? (node) => registerProgramRef(channel.id, item.entry.id, node)
              : undefined
          }
          channelId={channel.id}
          entry={item.entry}
          channelLogo={channel.logo}
          channelLiveChannel={channel.liveChannel}
          highlightLivePrograms={highlightLivePrograms}
          nowMs={nowMs}
          onTVFocusZoneEnter={onTVProgramsFocus}
          onTVProgramNavigateToDetail={onTVProgramNavigateToDetail}
          onTVEpgProgramFocus={onTVEpgProgramFocus}
          onProgramFocus={onProgramFocus}
          hasTVPreferredFocus={
            preferredFocusChannelId != null &&
            channel.id === preferredFocusChannelId &&
            preferredFocusProgramIndex != null &&
            index === preferredFocusProgramIndex
          }
        />
      ),
      [
        channel.id,
        channel.logo,
        channel.liveChannel,
        highlightLivePrograms,
        nowMs,
        onProgramFocus,
        onTVEpgProgramFocus,
        onTVProgramNavigateToDetail,
        onTVProgramsFocus,
        preferredFocusChannelId,
        preferredFocusProgramIndex,
        registerProgramRef,
      ],
    );

    const programListKey = getEpgChannelProgramListKey(
      id,
      programListRemountKey,
    );

    const viewportHeightRef = useRef(0);
    const centredRef = useRef<{
      listKey: string;
      height: number;
      corrected: boolean;
    } | null>(null);
    const pendingCentreRef = useRef<number | null>(null);

    const centreAnchorRow = useCallback(() => {
      const viewportHeight = viewportHeightRef.current;

      if (anchorIndex == null || !getItemLayout || viewportHeight <= 0) {
        return;
      }

      const centred = centredRef.current;

      if (centred?.listKey === programListKey) {
        if (centred.height === viewportHeight) {
          return;
        }
        // The grid measures its height after the first render, so the first
        // centring can use a provisional one. Correct it once, then leave the
        // list alone: a rotation or a resize must not undo the user's scroll.
        if (centred.corrected) {
          return;
        }
      }

      centredRef.current = {
        listKey: programListKey,
        height: viewportHeight,
        corrected: centred?.listKey === programListKey,
      };

      const { offset, length } = getItemLayout(undefined, anchorIndex);

      // The grid measures its height after the first render, so a column lays
      // out twice. Drop the superseded scroll: two of them read as a jump.
      if (pendingCentreRef.current != null) {
        cancelAnimationFrame(pendingCentreRef.current);
      }

      pendingCentreRef.current = requestAnimationFrame(() => {
        pendingCentreRef.current = null;
        programListRef.current?.scrollToOffset({
          offset: Math.max(0, offset - (viewportHeight - length) / 2),
          animated: false,
        });
      });
    }, [anchorIndex, getItemLayout, programListKey]);

    /* Drop a scroll that is still queued when the column goes away. */
    useEffect(
      () => () => {
        if (pendingCentreRef.current != null) {
          cancelAnimationFrame(pendingCentreRef.current);
        }
      },
      [],
    );

    const handleProgramListLayout = useCallback(
      (e: LayoutChangeEvent) => {
        viewportHeightRef.current = e.nativeEvent.layout.height;
        // Content size can settle before layout does, and that call leaves early
        // without a height, so the layout pass has to centre the row itself.
        centreAnchorRow();
      },
      [centreAnchorRow],
    );

    return (
      <View style={[$.root, { width: columnWidth }]}>
        {channelIndex != null ? (
          <ContextSelectedHeader
            channelIndex={channelIndex}
            title={title}
            logo={logo}
            logoDark={logoDark}
          />
        ) : (
          <EPGChannelHeader title={title} logo={logo} logoDark={logoDark} />
        )}

        <FlatList<FixedRowProgramLayout>
          key={programListKey}
          ref={programListRef}
          data={programLayouts}
          keyExtractor={({ entry }) => String(entry.id)}
          renderItem={renderProgramItem}
          nestedScrollEnabled
          showsVerticalScrollIndicator={false}
          scrollAnimationEnabled={CustomPlatform.select({
            tvOS: false,
            default: true,
          })}
          style={$.programList}
          contentContainerStyle={$.programListContent({ tabBarHeight })}
          onLayout={handleProgramListLayout}
          onContentSizeChange={centreAnchorRow}
          initialScrollIndex={anchorIndex}
          fadingEdgeLength={FOCUS_SCROLL_INSET}
          initialNumToRender={16}
          maxToRenderPerBatch={16}
          windowSize={PROGRAM_WINDOW_SIZE}
          removeClippedSubviews={CustomPlatform.select({
            web: false,
            tv: false,
            default: true,
          })}
          accessibilityRole="list"
          getItemLayout={getItemLayout}
          onScrollToIndexFailed={({ index }) => {
            const offset = getItemLayout
              ? getItemLayout(undefined, index).offset
              : index * compactRowHeight;
            programListRef.current?.scrollToOffset({
              offset: Math.max(0, offset),
              animated: false,
            });
          }}
        />
      </View>
    );
  },
);

export default EPGChannelColumn;
